import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import {
  Button, Field, PhoneField, Select, ErrorNote, Confirm,
} from "../../components/ui";
import { US_STATES, COUNTRIES, isUsState, isCountry } from "../../lib/places";
import { NameField, TextField, StateSelect, LicenseNumberField } from "../../components/fields";
import AddressField from "./AddressField";
import { SaveBar, SaveError, SaveStatus } from "../../components/save";
import { mustSave, useSaveState } from "../../lib/saving";
import type { AddressParts } from "../../lib/addressLookup";

const ID_LABELS: Record<string, string> = {
  drivers_license: "License number",
  state_id: "State ID number",
  passport: "Passport number",
  military_id: "Military ID number",
};
const ID_LABEL_FOR = (k: string) => ID_LABELS[k] ?? "Number";
import { loadCounties, surtaxLabel, type County } from "../../lib/counties";
import { countyForZip } from "../../lib/zipCounty";
import { checkRequired, checkPhone, checkEmail, checkZip } from "../../lib/validation";
import { customerName, prettyPhone } from "./CustomerList";

interface Customer {
  id: string;
  first_name: string; middle_name: string | null; last_name: string; suffix: string | null;
  date_of_birth: string;
  id_type: string; id_number: string; id_state: string; id_expiration: string | null;
  street: string; city: string; county: string | null; state: string; zip: string;
  phone_cell: string | null; phone_home: string | null; email: string | null;
  created_at: string;
}

/** The edit form as it would be for this row, untouched. */
const formFrom = (row: Customer) => ({
  firstName: row.first_name, middleName: row.middle_name ?? "",
  lastName: row.last_name, suffix: row.suffix ?? "",
  idType: row.id_type ?? "drivers_license",
  idNumber: row.id_number, idState: row.id_state,
  street: row.street, city: row.city, county: row.county ?? "",
  state: row.state, zip: row.zip,
  phoneCell: row.phone_cell ?? "", phoneHome: row.phone_home ?? "",
  email: row.email ?? "",
});

export default function CustomerDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const [c, setC] = useState<Customer | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [counties, setCounties] = useState<County[]>([]);
  const [errs, setErrs] = useState<Record<string, string | null>>({});

  const [f, setF] = useState({
    firstName: "", middleName: "", lastName: "", suffix: "",
    idType: "drivers_license",
    idNumber: "", idState: "", street: "", city: "", county: "", state: "", zip: "",
    phoneCell: "", phoneHome: "", email: "",
  });

  useEffect(() => { loadCounties().then(setCounties).catch(() =>
      setErr("Could not load the county list. Tax rates cannot be chosen "
        + "until it loads — check your connection and reopen this page.")); }, []);

  useEffect(() => {
    supabase.from("customers").select("*").eq("id", id).single()
      .then(({ data, error }) => {
        if (error) { setLoadErr("Could not load this customer."); return; }
        const row = data as Customer;
        setC(row);
        setF(formFrom(row));
      });
  }, [id]);

  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));

  const dirty = editing && c !== null && JSON.stringify(f) !== JSON.stringify(formFrom(c));
  const save_ = useSaveState({ dirty, what: "the customer changes" });
  const removal = useSaveState();
  const busy = save_.saving || removal.saving;

  /* Filling the county from the ZIP saves a scroll through 67 options, and
     the surtax depends on getting it right. Always a suggestion, never a lock. */
  function zipChanged(v: string) {
    setF((s) => {
      const next = { ...s, zip: v };
      const hit = countyForZip(v);
      if (hit && s.state.toUpperCase() === "FL") next.county = hit.county;
      return next;
    });
  }

  async function save() {
    const next = {
      firstName: checkRequired(f.firstName, "First name"),
      lastName: checkRequired(f.lastName, "Last name"),
      street: checkRequired(f.street, "Street"),
      city: checkRequired(f.city, "City"),
      zip: checkZip(f.zip),
      phoneCell: checkPhone(f.phoneCell),
      email: checkEmail(f.email),
      county: f.state.toUpperCase() === "FL" && !f.county ? "Pick a county." : null,
    };
    setErrs(next);
    if (!Object.values(next).every((e) => e === null)) {
      save_.fail("Fix the fields marked in red, then press Save again.");
      return;
    }

    const patch = {
      first_name: f.firstName.trim(), middle_name: f.middleName.trim() || null,
      last_name: f.lastName.trim(), suffix: f.suffix.trim() || null,
      id_type: f.idType,
      id_number: f.idNumber.trim(), id_state: f.idState.toUpperCase(),
      street: f.street.trim(), city: f.city.trim(),
      county: f.county || null, state: f.state.toUpperCase(), zip: f.zip.trim(),
      phone_cell: f.phoneCell || null, phone_home: f.phoneHome || null,
      email: f.email.trim() || null,
    };
    await save_.run(async () => {
      mustSave(await supabase.from("customers").update(patch).eq("id", id).select("id"));
      setC({ ...(c as Customer), ...patch } as Customer);
      setEditing(false);
    }, { toast: "Customer saved" });
  }

  function cancel() {
    if (dirty && !window.confirm("Discard your changes to this customer?")) return;
    if (c) setF(formFrom(c));
    setEditing(false); setErrs({}); save_.dismiss();
  }

  async function remove() {
    // A customer attached to a deal is protected by a foreign key, on
    // purpose; describeDbError says so in those words.
    const ok = await removal.run(async () => {
      mustSave(await supabase.from("customers").delete().eq("id", id).select("id"));
    }, { toast: "Customer deleted" });
    setConfirming(false);
    if (ok) onBack();
  }

  if (loadErr) return <ErrorNote>{loadErr}</ErrorNote>;
  if (!c) return <p className="hint">Loading</p>;

  return (
    <>
      <div className="head"><h2>{customerName(c)}</h2></div>
      {!editing && save_.status === "saved" && (
        <p className="savenote"><SaveStatus save={save_} savedLabel="Customer saved" /></p>
      )}

      {!editing ? (
        <>
          <dl className="spec">
            <div><dt>Born</dt><dd>{c.date_of_birth}</dd></div>
            <div><dt>License</dt><dd className="mono">{c.id_number} ({c.id_state})</dd></div>
            <div><dt>Address</dt><dd>{c.street}</dd></div>
            <div><dt></dt><dd>{c.city}, {c.state} {c.zip}</dd></div>
            <div><dt>County</dt><dd>{c.county ?? "—"}</dd></div>
            <div><dt>Cell</dt><dd>{prettyPhone(c.phone_cell) || "—"}</dd></div>
            <div><dt>Home</dt><dd>{prettyPhone(c.phone_home) || "—"}</dd></div>
            <div><dt>Email</dt><dd>{c.email ?? "—"}</dd></div>
          </dl>

          {err && <ErrorNote>{err}</ErrorNote>}
          <SaveError save={removal} hint="The customer has not been deleted." />

          {confirming ? (
            <Confirm
              message={`Delete ${customerName(c)}? This cannot be undone.`}
              confirmLabel={busy ? "Deleting" : "Delete customer"}
              onConfirm={remove}
              onCancel={() => setConfirming(false)}
            />
          ) : (
            <div className="actions">
              <Button variant="ghost"
                      onClick={() => { setF(formFrom(c)); save_.reset(); setEditing(true); }}>
                Edit
              </Button>
              <Button variant="danger" onClick={() => setConfirming(true)}>Delete</Button>
            </div>
          )}
        </>
      ) : (
        <>
          {/* Same order as the add form, and as the license: first, middle,
              then last. */}
          <div className="grid2">
            <NameField label="First name" required value={f.firstName}
                              onChange={set("firstName")} error={errs.firstName} />
            <NameField label="Middle name" value={f.middleName} onChange={set("middleName")} />
          </div>
          <div className="grid2">
            <NameField label="Last name" required value={f.lastName}
                              onChange={set("lastName")} error={errs.lastName} />
            <TextField label="Suffix" value={f.suffix} onChange={set("suffix")} />
          </div>
          {/* The same identification block as the add form. This screen used
              to have neither the type nor a list to pick from, so a passport
              was edited as though it were a Florida license. */}
          <Select label="ID type" value={f.idType}
                  onChange={(v) => setF((s) => ({
                    ...s,
                    idType: v,
                    idState: v === "passport"
                      ? (isCountry(s.idState) ? s.idState : "")
                      : (isUsState(s.idState) ? s.idState : "FL"),
                  }))}>
            <option value="drivers_license">Driver license</option>
            <option value="state_id">State ID</option>
            <option value="passport">Passport</option>
            <option value="military_id">Military ID</option>
          </Select>
          <div className="grid2">
            <LicenseNumberField label={ID_LABEL_FOR(f.idType)} required value={f.idNumber}
                   onChange={set("idNumber")} idType={f.idType} idState={f.idState}
                   error={errs.idNumber} />
            {f.idType === "passport" ? (
              <Select label="Issuing country" value={f.idState} onChange={set("idState")}>
                <option value="">Choose</option>
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </Select>
            ) : (
              <Select label="Issuing state" value={f.idState} onChange={set("idState")}>
                <option value="">Choose</option>
                {US_STATES.map((s) => (
                  <option key={s.code} value={s.code}>{s.name}</option>
                ))}
              </Select>
            )}
          </div>

          {/* The same lookup as the add form. Editing an address by hand
              while adding one offered suggestions was an odd split. */}
          <AddressField
            value={f.street} onChange={set("street")} error={errs.street}
            onPick={(a: AddressParts) => setF((s) => ({
              ...s,
              city: a.city || s.city,
              state: a.state || s.state,
              zip: a.zip || s.zip,
              county: counties.some((c) => c.name === a.county) ? a.county : s.county,
            }))} />
          <div className="grid2">
            <Field label="City" value={f.city} onChange={set("city")} error={errs.city} />
            <Field label="ZIP" inputMode="numeric" maxLength={10} value={f.zip}
                   onChange={zipChanged} error={errs.zip} />
          </div>
          <div className="grid2">
            <StateSelect label="State" required value={f.state} onChange={set("state")} />
            <Select label="County" value={f.county} onChange={set("county")}
                    placeholder="Choose" error={errs.county}>
              {counties.map((x) => (
                <option key={x.name} value={x.name}>
                  {x.name} ({surtaxLabel(x.surtax_bps)})
                </option>
              ))}
            </Select>
          </div>

          <div className="grid2">
            <PhoneField label="Cell" value={f.phoneCell} onChange={set("phoneCell")}
                        error={errs.phoneCell} />
            <PhoneField label="Home" value={f.phoneHome} onChange={set("phoneHome")} />
          </div>
          <Field label="Email" type="email" value={f.email} onChange={set("email")}
                 error={errs.email} />


          {err && <ErrorNote>{err}</ErrorNote>}
          <SaveBar save={save_} onSave={() => void save()} label="Save changes"
                   idle="No changes yet">
            <Button variant="ghost" onClick={cancel} disabled={busy}>Cancel</Button>
          </SaveBar>
        </>
      )}
    </>
  );
}
